import React, { useEffect, useMemo, useState } from "react";

import type { ClientId } from "../../domain/types";
import {
  fetchOpeningBalances,
  pushOpeningBalances,
  saveOpeningBalances,
  type BalanceSide,
  type OpeningBalanceAccountDTO,
  type OpeningBalanceSheetDTO,
} from "../../data/openingBalancesApi";
import { APIError } from "../../data/http";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { cn } from "../../components/cn";
import { ChevronDownIcon, ChevronRightIcon } from "../../components/icons";

/**
 * Opening balances, entered the way Zoho's own screen enters them.
 *
 * A grid of the client's chart of accounts in six groups, a debit and a credit
 * column, receivable and payable above, and a running total that has to reach
 * zero before anything is sent. No template, no upload: the figures are typed
 * here and staged, and one button sends the lot.
 *
 * WHY EVERY EDIT GOES TO THE SERVER AND NOTHING GOES TO ZOHO. Typing a cell
 * POSTs it to `save`, which validates it and stores it against the client —
 * so a CA can close the tab halfway through a trial balance and find it again
 * tomorrow. `push` is the only call that reaches the client's books, and it is
 * behind a button that says where the figures are going.
 */

/** A cell as the CA has it right now, before it has been sent anywhere. */
type Draft = Record<string, { side: BalanceSide | null; amount: string }>;

function draftFrom(sheet: OpeningBalanceSheetDTO): Draft {
  const out: Draft = {};

  const put = (account: OpeningBalanceAccountDTO | null) => {
    if (account === null) return;
    // A locked row is not a cell: its figure is the documents' own total,
    // lives outside the entry, and must not join the entry's running total.
    if (account.locked === true) return;
    out[account.account_id] = {
      side: account.side,
      amount: account.amount === null ? "" : String(account.amount),
    };
  };

  put(sheet.receivable);
  put(sheet.payable);
  for (const group of sheet.groups) for (const account of group.accounts) put(account);

  return out;
}

/**
 * The column template every row on this grid shares — header, the locked AR/AP
 * rows, the account rows, and the Total. One constant rather than four copies,
 * because the four have to agree exactly: a column that disagrees by a rem reads
 * as a broken table.
 *
 * SIZED TO THE WIDEST FIGURE THE RANGE PERMITS, which is `MAX_INTEGER_DIGITS`
 * digits and two decimals: `99,99,99,99,99,999.99`, twenty-one characters once
 * Indian grouping puts five commas in it. Sizing to anything narrower is how the
 * amounts came to be cut off mid-figure in the first place.
 *
 * MEASURED IN INTER, which is the part worth writing down. `.co-root` sets
 * `font-family: Inter` for the whole onboarding flow (see `styles.css`), while
 * `index.css` sets Figtree on `body` — so a measuring probe appended to
 * `document.body` renders in the wrong face and reports numbers about 5% too
 * small. Inter is the wider of the two. Measured inside a row, by cloning a real
 * input and growing it until the figure stopped clipping:
 *
 *   text-xs (12px)   154px   ->  10rem floor, 10.5rem when there is room
 *   text-sm (14px)   177px   ->  12rem, only where the card is wide enough for it
 *
 * Grid sizes the non-flexible tracks before the flexible one, so the amounts
 * reach their maximum wherever there is room and give width back before the name
 * does.
 *
 * THE NAME COLUMN HAS ITS OWN FLOOR, and needs it. As `minmax(0,1fr)` it was the
 * only flexible track, so it paid for everything: at 1024px the content column
 * is 384px, the amount columns and their gaps wanted 352px of it, and `1fr`
 * obliged by going to zero — the names vanished and the amount columns, unable
 * to shrink, walked out through the card's right border. A grid overflows its
 * container silently, every cell still measuring as if it fits, which is how
 * that got missed.
 */
const AMOUNT_GRID =
  "grid-cols-[minmax(6rem,1fr)_minmax(10rem,10.5rem)_minmax(10rem,10.5rem)] " +
  "min-[1366px]:grid-cols-[minmax(6rem,1fr)_12rem_12rem]";

/**
 * The width below which the grid stops shrinking and starts scrolling.
 *
 * 28.5rem = 456px = the 6rem name floor, plus two 10rem amount floors, plus the
 * 0.5rem gaps and the rows' own `px-3`. Below that every track is already at its
 * minimum and something has to give; this decides that the something is a
 * scrollbar inside the card rather than the name column or the card's border.
 *
 * Applied once, to the wrapper every row sits in, rather than per row: the header
 * and the account rows carry `px-3` and the Total row does not, so a `min-w` on
 * each would leave the Total 24px out of step with the columns above it the
 * moment the floor engaged.
 */
const AMOUNT_GRID_FLOOR = "min-w-[28.5rem]";

/**
 * Every figure IN A COLUMN, so they shrink together — the inputs, the locked
 * AR/AP figures and the Total. If one kept the larger size it would be the one
 * that overflows.
 *
 * THE SIZE STEPS UP WHERE THE CARD DOES, at 1366px, and the two are the same
 * decision. `text-sm` needs 177px of column for the widest permitted figure, and
 * at 1280px there is no room for that without taking it from the account names;
 * at 1366px the card widens (see `OpeningBalancesStep`) and pays for both.
 *
 * The group-header subtotal is deliberately not on this list: it sits `ml-auto`
 * in a flex button rather than in a track, so its width cannot push a column
 * around.
 */
const AMOUNT_TEXT = "text-xs tabular-nums min-[1366px]:text-sm";

/**
 * THE RANGE, AND WHY IT IS THIS AND NOT WIDER.
 *
 * 13 digits before the decimal and 2 after is 15 significant digits, which is
 * the most a JavaScript `Number` holds exactly. Past it, arithmetic is silently
 * approximate: `Number("11111111111234567")` is `11111111111234568`, and that
 * one-rupee lie was the whole defect — the figure was altered on the way in, the
 * DB clamped it again at `decimal(18,2)`, and a CA got three different numbers
 * and no error. Anything inside this range survives typing, totalling, staging
 * and the push unchanged.
 *
 * ₹99,999 crore, for reference. If it ever has to grow, `Number` is the ceiling:
 * beyond 15 digits the arithmetic has to stop being floating point, on both
 * sides, before the limit can move.
 */
const MAX_INTEGER_DIGITS = 13;
const MAX_DECIMALS = 2;

/**
 * Why a typed figure is refused, or null when it is fine.
 *
 * Judges the TEXT, not a parsed number — by the time it is a `Number` the
 * rounding has already happened and there is nothing left to detect. Reads the
 * same decorations `parseAmount` does, so a figure pasted out of a spreadsheet
 * as "₹ 1,00,000.00" or "(2,500)" is judged on its digits.
 */
function amountError(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;

  const inner = trimmed.startsWith("(") && trimmed.endsWith(")") ? trimmed.slice(1, -1) : trimmed;
  const clean = inner.replace(/[^0-9.-]/g, "").replace(/^-/, "");

  if (clean === "" || !/^\d*(\.\d*)?$/.test(clean)) {
    return "That is not an amount.";
  }

  const [whole = "", decimals = ""] = clean.split(".");
  if (whole.replace(/^0+/, "").length > MAX_INTEGER_DIGITS) {
    return `Amount too large — up to ${MAX_INTEGER_DIGITS} digits before the decimal`;
  }
  if (decimals.length > MAX_DECIMALS) {
    return `Up to ${MAX_DECIMALS} decimal places`;
  }

  return null;
}

/** Indian grouping, which is what every figure on this screen is read in. */
function formatAmount(value: number): string {
  return value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * The same reading of a typed amount the server does, so the running total
 * agrees with the one the push will be checked against. Anything unparseable
 * counts as nothing rather than as NaN — the server is what refuses it, and it
 * says why.
 */
function parseAmount(text: string): number {
  const trimmed = text.trim();
  if (trimmed === "") return 0;

  const negative = trimmed.startsWith("(") && trimmed.endsWith(")");
  const clean = trimmed.replace(/[^0-9.-]/g, "");
  const value = Number(clean);

  if (!Number.isFinite(value)) return 0;

  return negative ? -value : value;
}

/**
 * One figure as a single signed number: debits positive, credits negative.
 *
 * The one place the "a negative amount is the other side" rule lives, so the
 * running total, a group subtotal and the has-this-changed check cannot come to
 * three different answers about the same cell. Zoho's own screen states the
 * rule the same way: "enter a negative value for a credit amount using (-)".
 */
function signedValue(side: BalanceSide | null, amount: string | number | null): number {
  if (side === null || amount === null) return 0;

  const value = typeof amount === "number" ? amount : parseAmount(amount);
  if (value === 0) return 0;

  const effective = value < 0 ? (side === "debit" ? "credit" : "debit") : side;

  return (effective === "credit" ? -1 : 1) * Math.abs(value);
}

/**
 * Debits and credits as the screen shows them — GROSS: every typed cell plus
 * the locked document-derived AR/AP rows. The CA types the legacy trial
 * balance exactly as it prints, equity included, and the familiar
 * debits-equal-credits check is the whole rule.
 *
 * The entry then goes to Zoho short of the documents' figures, deliberately:
 * Zoho plugs the entry's gap and counterweights the Retained Earnings it
 * computes from the pre-migration documents, and the two cancel to the rupee
 * — the balance sheet lands on the legacy figures with no suspense. Proven
 * live both ways on 2026-08-17 (docs/onboarding-outstanding-invoices.md).
 * Matches the server's sheet() arithmetic, which is what gates the push.
 */
function totalsFrom(
  draft: Draft,
  sheet: OpeningBalanceSheetDTO | null
): { debit: number; credit: number; difference: number; balanced: boolean } {
  let debit = 0;
  let credit = 0;

  for (const cell of Object.values(draft)) {
    // A refused figure is not staged, so it is not part of the entry either.
    // Counting it would put a number in the Total that the server has never
    // seen, which is the same class of lie this range check exists to stop.
    if (amountError(cell.amount) !== null) continue;

    const value = signedValue(cell.side, cell.amount);
    if (value > 0) debit += value;
    else credit += -value;
  }

  if (sheet !== null) {
    for (const locked of [sheet.receivable, sheet.payable]) {
      if (locked?.locked !== true || locked.amount === null) continue;
      if (locked.side === "debit") debit += locked.amount;
      else credit += locked.amount;
    }
  }

  const difference = Math.round((debit - credit) * 100) / 100;

  return { debit, credit, difference, balanced: Math.abs(difference) < 0.005 };
}

// ── Cells ───────────────────────────────────────────────────────────────────

/** What a row needs. The side is a property of the cell, not of the row. */
interface RowProps {
  account: OpeningBalanceAccountDTO;
  draft: Draft;
  disabled: boolean;
  focusKey: string | null;
  onFocus: (key: string | null) => void;
  onChange: (accountId: string, side: BalanceSide, amount: string) => void;
  onCommit: (accountId: string) => void;
}

interface CellProps extends RowProps {
  side: BalanceSide;
}

function AmountCell({ account, side, draft, disabled, focusKey, onFocus, onChange, onCommit }: CellProps) {
  const cell = draft[account.account_id];
  const key = `${account.account_id}:${side}`;
  const focused = focusKey === key;

  // A cell shows a figure only when this is the side the figure is on, so a
  // debit typed into the debit box empties the credit box beside it rather than
  // leaving the same number in both.
  const raw = cell?.side === side ? cell.amount : "";
  const parsed = parseAmount(raw);
  const refused = amountError(raw) !== null;

  // Exactly the keystrokes while the cell has focus, and exactly the keystrokes
  // afterwards too if the figure was refused — formatting a number we would not
  // accept is how a CA came to see a different figure from the one they typed.
  // Grouping is applied on blur, and only to a figure that is going to be saved.
  const display =
    focused || refused || raw === "" || parsed === 0 ? raw : formatAmount(Math.abs(parsed));

  return (
    <input
      inputMode="decimal"
      value={display}
      disabled={disabled}
      aria-label={`${account.account_name} ${side}`}
      onFocus={() => onFocus(key)}
      onChange={(event) => onChange(account.account_id, side, event.target.value)}
      onBlur={() => {
        onFocus(null);
        onCommit(account.account_id);
      }}
      aria-invalid={refused || undefined}
      className={cn(
        "h-8 w-full rounded-md border bg-background px-2 text-right outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50",
        refused && "border-destructive text-destructive focus-visible:ring-destructive/50",
        AMOUNT_TEXT
      )}
    />
  );
}

/**
 * A receivable or payable that outstanding documents have taken over.
 *
 * Read-only by nature rather than by attribute: the figure is the documents'
 * own total and the only way to change it is to change the documents. Shown in
 * the same columns as everything else so the eye can still run down a side —
 * but it is NOT part of the entry's total below, because a real invoice
 * carries its receivable outside the opening balance.
 */
function LockedRow({ account, documents, className }: {
  account: OpeningBalanceAccountDTO;
  documents: number;
  className?: string;
}) {
  const amount = account.amount ?? 0;

  return (
    <div className={cn("grid items-center gap-2 px-3 py-1.5", AMOUNT_GRID, className)}>
      <div className="min-w-0">
        <span className="truncate text-sm" title={account.account_name}>{account.account_name}</span>
        <span className="block text-xs text-muted-foreground">
          From {documents} outstanding {documents === 1 ? "document" : "documents"}, counted in the totals
        </span>
      </div>
      <span className={cn("text-right text-muted-foreground", AMOUNT_TEXT)}>
        {account.side === "debit" ? formatAmount(amount) : ""}
      </span>
      <span className={cn("text-right text-muted-foreground", AMOUNT_TEXT)}>
        {account.side === "credit" ? formatAmount(amount) : ""}
      </span>
    </div>
  );
}

/** One account. The "was" line only appears when this differs from Zoho. */
function AccountRow(props: RowProps & { className?: string }) {
  const { account, draft } = props;
  const cell = draft[account.account_id];

  // Both sides as one signed number, so "1,000 Dr" and "-1,000 Cr" compare
  // equal and a side flip on the same magnitude still counts as a change.
  const staged = signedValue(cell?.side ?? null, cell?.amount ?? null);
  const inZoho = signedValue(account.zoho_side, account.zoho_amount);
  const changed = Math.abs(staged - inZoho) >= 0.005;
  const refusal = amountError(cell?.amount ?? "");

  return (
    <div className={cn("grid items-center gap-2 px-3 py-1.5", AMOUNT_GRID, props.className)}>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm" title={account.account_name}>{account.account_name}</span>
          {!account.is_active && (
            <span className="shrink-0 rounded-sm bg-muted px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
              Inactive
            </span>
          )}
        </div>
        {/* The refusal displaces the Zoho comparison rather than joining it: the
            figure on screen is not going anywhere, so what Zoho holds is not
            the thing to read next. One message per row, because a row carries
            one figure — the side is part of the cell, not a second cell. */}
        {refusal !== null ? (
          <span className="text-xs text-destructive">{refusal}</span>
        ) : changed && account.zoho_amount !== null ? (
          <span className="text-xs text-muted-foreground">
            Zoho holds {formatAmount(account.zoho_amount)} {account.zoho_side === "credit" ? "Cr" : "Dr"}
          </span>
        ) : changed && account.zoho_amount === null ? (
          <span className="text-xs text-muted-foreground">Not in Zoho yet</span>
        ) : null}
      </div>
      <AmountCell {...props} side="debit" />
      <AmountCell {...props} side="credit" />
    </div>
  );
}

// ── The grid ────────────────────────────────────────────────────────────────

export function OpeningBalanceGrid({ clientId, reloadToken = 0, onBalancedChange, beforeGroups }: {
  clientId: ClientId;
  /**
   * Bumped by the parent when the outstanding-documents panels change what is
   * staged or pushed, so the locked AR/AP figures here move with them. A
   * reload reseeds the draft, which is safe: every typed cell was already
   * persisted on blur.
   */
  reloadToken?: number;
  /**
   * Whether debits and credits agree, reported as the CA types. The step gates
   * its Continue button on this: a sheet that does not tally must be fixed here,
   * not carried forward. An untouched sheet is balanced (0 = 0) so a client with
   * no opening balances is never blocked.
   */
  onBalancedChange?: (balanced: boolean) => void;

  /**
   * Rendered between the receivable/payable section and the first account
   * group. The step owns what goes here; the grid only owns where.
   */
  beforeGroups?: React.ReactNode;
}) {
  const [sheet, setSheet] = useState<OpeningBalanceSheetDTO | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [focusKey, setFocusKey] = useState<string | null>(null);

  /**
   * Bumped only when the sheet should replace what is on screen — the first
   * load and a push. A `save` response also carries a whole sheet, but reseeding
   * the draft from it would fight whatever the CA typed while it was in flight.
   */
  const [seed, setSeed] = useState(0);

  /**
   * Which GST registration the grid is showing. Null is the primary, which is
   * the only answer for a client with one registration — and the one this
   * screen had before registrations existed.
   *
   * Switching refetches rather than filtering something already loaded: the
   * figures, the totals and what Zoho holds are all per registration, and the
   * server is the only thing that knows them.
   */
  const [locationId, setLocationId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;

    void (async () => {
      try {
        const next = await fetchOpeningBalances(clientId, locationId);
        if (!live) return;

        setSheet(next);
        setDraft(draftFrom(next));
        // Groups that already have something in them open themselves; the rest
        // stay shut, because thirty-six expense accounts nobody is using is a
        // wall rather than a form.
        setOpen(
          Object.fromEntries(
            next.groups.map((group) => [
              group.key,
              group.accounts.some((account) => account.amount !== null || account.zoho_amount !== null),
            ])
          )
        );
        setLoadError(null);
      } catch (error) {
        if (live) setLoadError(error instanceof Error ? error.message : "Could not load the opening balances.");
      } finally {
        if (live) setLoading(false);
      }
    })();

    return () => {
      live = false;
    };
  }, [clientId, seed, reloadToken, locationId]);

  const totals = useMemo(() => totalsFrom(draft, sheet), [draft, sheet]);

  /**
   * The registrations that do not balance, other than the one on screen.
   *
   * The grid holds one registration's figures, so the one being looked at is
   * judged live from the draft — it has to react as a cell is typed — and the
   * rest from what the server last said, which is refreshed by every accepted
   * save. A registration with nothing entered is not unbalanced; it has
   * nothing to balance.
   */
  const unbalancedElsewhere = useMemo(() => {
    if (sheet === null) return [];
    const here = locationId ?? sheet.location_id;

    return (sheet.registration_totals ?? []).filter(
      (registration) =>
        registration.location_id !== here && registration.has_lines && !registration.balanced
    );
  }, [sheet, locationId]);

  const refusedCells = useMemo(
    () => Object.values(draft).filter((cell) => amountError(cell.amount) !== null).length,
    [draft]
  );

  // Continue is gated on this as well as on the send button, and deliberately.
  // A refused cell was never saved, so letting a CA walk forward past one is
  // how the figure they typed disappears without them being told — the failure
  // this ticket exists to remove, arrived at from the other direction.
  useEffect(() => {
    onBalancedChange?.(totals.balanced && refusedCells === 0 && unbalancedElsewhere.length === 0);
  }, [totals.balanced, refusedCells, unbalancedElsewhere, onBalancedChange]);

  function edit(accountId: string, side: BalanceSide, amount: string) {
    setDraft((current) => ({ ...current, [accountId]: { side, amount } }));
    setPushed(null);
  }

  /** Send one cell to be staged. Not to Zoho — `save` never leaves our server. */
  async function commit(accountId: string) {
    const cell = draft[accountId];
    if (cell === undefined) return;

    // Refused figures are not staged. The cell keeps showing what was typed,
    // with the reason beside it, and the stored row is left exactly as it was —
    // the server enforces the same range, so this only saves a round trip.
    if (amountError(cell.amount) !== null) return;

    try {
      const next = await saveOpeningBalances(
        clientId,
        [{ account_id: accountId, side: cell.side ?? "debit", amount: cell.amount }],
        undefined,
        locationId
      );
      setSheet(next);
      setSaveError(null);
    } catch (error) {
      setSaveError(
        error instanceof APIError || error instanceof Error
          ? error.message
          : "Could not save that figure."
      );
    }
  }

  async function send() {
    setPushing(true);
    setSaveError(null);

    try {
      const next = await pushOpeningBalances(clientId, locationId);
      setSheet(next);
      setDraft(draftFrom(next));
      const saves = next.push?.saves ?? 1;
      const scope =
        saves > 1
          ? `${next.push?.sent ?? 0} accounts across ${saves} GST registrations`
          : `${next.push?.sent ?? 0} accounts`;
      setPushed(
        next.push?.created
          ? `Opening balance entered in Zoho Books (${scope}).`
          : `Opening balance replaced in Zoho Books (${scope}).`
      );
      setSeed((value) => value + 1);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Could not send the opening balances.");
    } finally {
      setPushing(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading the chart of accounts…</p>;
  }

  if (loadError !== null || sheet === null) {
    return <Callout variant="warning">{loadError ?? "Could not load the opening balances."}</Callout>;
  }

  const cellProps = {
    draft,
    disabled: pushing,
    focusKey,
    onFocus: setFocusKey,
    onChange: edit,
    onCommit: (accountId: string) => void commit(accountId),
  };

  const documentsLocked = sheet.outstanding.receivable_locked || sheet.outstanding.payable_locked;

  // Only worth a choice when there is one to make. A client with a single GST
  // registration sees the grid it has always seen.
  const registrations = sheet.registrations ?? [];
  const showing = locationId ?? sheet.location_id;

  return (
    <div className="flex flex-col gap-4">
      {registrations.length > 1 && (
        <div className="flex flex-col gap-2 rounded-md border p-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">GST registration</span>
            <select
              value={showing ?? ""}
              disabled={pushing}
              onChange={(event) => {
                setLocationId(event.target.value);
                setPushed(null);
                setSaveError(null);
              }}
              className="h-9 rounded-md border bg-background px-2 text-sm"
            >
              {registrations.map((registration) => (
                <option key={registration.location_id} value={registration.location_id}>
                  {registration.name}
                  {registration.gstin === null ? "" : ` — ${registration.gstin}`}
                  {registration.is_primary ? " (primary)" : ""}
                </option>
              ))}
            </select>
          </div>
          {/*
            Zoho files an opening balance per location and balances each one
            separately, with its own adjustment line — so this is not a filter
            over one set of figures, it is a different set. Said out loud
            because the grid looks identical either way.
          */}
          <p className="text-xs text-muted-foreground">
            Each registration carries its own opening balance and has to balance on its own. Switch to enter the
            others; sending writes all of them.
          </p>
        </div>
      )}
      {/*
        Called what the Start date step calls it, not what Zoho calls it — Zoho
        labels the same field "Migration Date" on its own opening-balance form,
        and the API key stays `migration_date`. There is one of these per entry
        because Zoho holds one.

        READ-ONLY HERE, deliberately. The Start date step owns this value, and
        every outstanding document above is validated against it; an editable
        field on this screen would let one keystroke move the goalposts under
        rows a CA already uploaded.
      */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/20 p-3">
        <span className="text-sm font-medium">Account start date</span>
        <span className="text-sm tabular-nums">{sheet.migration_date ?? "Not set"}</span>
        <span className="text-xs text-muted-foreground">
          {sheet.migration_date === null
            ? "Set it on the Start date step; documents and balances are all dated against it."
            : "From the Start date step. Every document above must be dated before it."}
        </span>
      </div>

      {saveError !== null && <Callout variant="warning">{saveError}</Callout>}
      {pushed !== null && <Callout variant="info">{pushed}</Callout>}

      {/*
        One scroll container around everything that shares the column template —
        header, AR/AP, the groups, the Total. It has to be one, and it has to
        include the Total: the columns only read as columns if all four rows move
        together, so a Total left outside would slide out of alignment the moment
        anyone scrolled.
      */}
      <div className="overflow-x-auto">
        <div className={cn("flex flex-col gap-4", AMOUNT_GRID_FLOOR)}>
          <div className={cn("grid gap-2 px-3 text-xs font-medium uppercase tracking-wide text-muted-foreground", AMOUNT_GRID)}>
            <span>Account</span>
            <span className="text-right">Debit</span>
            <span className="text-right">Credit</span>
          </div>

          {/* Receivable and payable, which Zoho keeps out of the account grid.
              A side with outstanding documents behind it is derived, not typed. */}
          <section className="rounded-md border">
            {sheet.receivable &&
              (sheet.receivable.locked === true ? (
                <LockedRow account={sheet.receivable} documents={sheet.outstanding.invoice_documents} />
              ) : (
                <AccountRow account={sheet.receivable} {...cellProps} />
              ))}
            {sheet.payable &&
              (sheet.payable.locked === true ? (
                <LockedRow
                  account={sheet.payable}
                  documents={sheet.outstanding.bill_documents}
                  className="border-t"
                />
              ) : (
                <AccountRow account={sheet.payable} {...cellProps} className="border-t" />
              ))}
            <p className="border-t px-3 py-2 text-xs text-muted-foreground">
              {documentsLocked
                ? "Taken from the documents above. Zoho receives these figures as the documents, not in this entry."
                : "One figure each, the way Zoho takes them. Zoho holds a single receivable total whether it is typed here or built up from per-customer balances. Once outstanding invoices and bills are staged above, these cells lock into totals derived from them."}
            </p>
          </section>

          {/* A slot, not a hard-coded panel: the grid's job is the figures, and
              what goes here — today the bulk upload — belongs to the step. It
              sits inside the scroll wrapper on purpose, so its edges line up with
              the section above and the groups below at every width, and it moves
              with them when the grid is narrower than its floor. */}
          {beforeGroups}

          {sheet.groups.map((group) => {
            const accounts = group.accounts;
            if (accounts.length === 0) return null;

            const expanded = open[group.key] === true;

            const groupTotal = accounts.reduce((sum, account) => {
              const cell = draft[account.account_id];

              return sum + signedValue(cell?.side ?? null, cell?.amount ?? null);
            }, 0);

            return (
              <section key={group.key} className="rounded-md border">
                <button
                  type="button"
                  onClick={() => setOpen((current) => ({ ...current, [group.key]: !expanded }))}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/40"
                >
                  {expanded ? (
                    <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium">{group.label}</span>
                  <span className="text-xs text-muted-foreground">{accounts.length}</span>
                  {Math.abs(groupTotal) >= 0.005 && (
                    <span className="ml-auto text-sm tabular-nums text-muted-foreground">
                      {formatAmount(Math.abs(groupTotal))} {groupTotal < 0 ? "Cr" : "Dr"}
                    </span>
                  )}
                </button>

                {expanded && (
                  <div className="divide-y border-t">
                    {accounts.map((account) => (
                      <AccountRow key={account.account_id} account={account} {...cellProps} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}

          {/* The total, and the reason the button is on or off. */}
          <div className="flex flex-col gap-2 rounded-md border bg-muted/20 p-3">
            <div className={cn("grid gap-2 text-sm font-medium", AMOUNT_GRID)}>
              <span>Total</span>
              <span className={cn("text-right", AMOUNT_TEXT)}>{formatAmount(totals.debit)}</span>
              <span className={cn("text-right", AMOUNT_TEXT)}>{formatAmount(totals.credit)}</span>
            </div>
            {/* A refusal is read before the tally: the total is missing a figure,
                so whether it agrees is not yet a meaningful thing to report. */}
            {refusedCells > 0 ? (
              <p className="text-sm text-destructive">
                {refusedCells === 1
                  ? "One figure has not been accepted. Fix it before sending — it is not saved, and not in the total."
                  : `${refusedCells} figures have not been accepted. Fix them before sending — they are not saved, and not in the total.`}
              </p>
            ) : totals.balanced ? (
              <p className="text-sm text-emerald-700 dark:text-emerald-400">Debits and credits agree.</p>
            ) : (
              <p className="text-sm text-amber-800 dark:text-amber-400">
                Difference {formatAmount(Math.abs(totals.difference))}. Debits and credits must agree before this
                can be sent. Zoho would take it as it stands and post the difference to its own Opening Balance
                Adjustments account.
              </p>
            )}
            {/*
              THE GROSS RULE (docs/onboarding-outstanding-invoices.md, settled by
              live probe 2026-08-17). With the locked AR/AP counted here, a
              balanced screen means the entry goes to Zoho short of the documents'
              net — deliberately. Zoho plugs that gap in its Opening Balance
              Adjustments and counterweights the Retained Earnings it computes
              from the pre-migration documents; the legs cancel to the rupee, and
              the client's balance sheet lands on the legacy figures with no
              suspense. Entering equity NET instead (the pre-probe rule) strands
              the documents' net in Opening Balance Adjustments as a liability.
            */}
            {documentsLocked && (
              <p className="text-xs text-muted-foreground">
                Enter everything else, equity included,{" "}
                <span className="font-medium">exactly as the legacy books show it</span>. Zoho squares the
                entry with the documents on its side.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <p className="text-xs text-muted-foreground">
            {sheet.zoho.entered
              ? `Zoho already holds an opening balance dated ${sheet.zoho.date ?? "-"}. Sending replaces it.`
              : "Nothing has been sent to Zoho yet."}
          </p>
          {/*
            One send writes every registration, so one unbalanced registration
            stops all of them — and the CA cannot see it, because the grid is
            showing a different one. Naming it beside the button is the whole
            point: "the button is off" without saying which branch is out
            sends somebody hunting through the selector.
          */}
          {unbalancedElsewhere.length > 0 && (
            <p className="text-xs text-amber-800 dark:text-amber-400">
              {unbalancedElsewhere
                .map(
                  (registration) =>
                    `${registration.name} is out by ${formatAmount(Math.abs(registration.difference))}`
                )
                .join("; ")}
              . Every GST registration has to agree on its own before the entry can be sent.
            </p>
          )}
        </div>
        <CoButton
          onClick={() => void send()}
          disabled={pushing || !totals.balanced || refusedCells > 0 || unbalancedElsewhere.length > 0}
          className="w-fit"
        >
          {pushing ? "Sending…" : sheet.zoho.entered ? "Replace in Zoho Books" : "Send to Zoho Books"}
        </CoButton>
      </div>
    </div>
  );
}
